import { LitheElement, LitheFC } from '@wheelbox/lithejs-engine';
import withTranslations from '@wheelbox/lithejs-i18n';
import { goto } from '@wheelbox/lithejs-router';

import { Button } from '@wheelbox/lithejs-ui';
import AnimatedTerminal from '@components/shared/animated-terminal';

export interface HeroSectionProps {
  id?: string;
  testId?: string;
}

const HeroSection: LitheFC<HeroSectionProps> = ({
  id = 'hero-section',
  testId = 'hero-section',
}): LitheElement<'section'> => {
  const t = withTranslations('Home');

  return [
    'section',
    {
      id,
      'data-testid': testId,
      class:
        'relative min-h-[85vh] lg:min-h-[90vh] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-24',
    },
    // Background gradient
    [
      'div',
      {
        class:
          'absolute inset-0 bg-gradient-to-b from-primary/5 via-primary/2 to-transparent -z-10',
        'aria-hidden': 'true',
      },
    ],
    // Content container
    [
      'div',
      { class: 'max-w-5xl mx-auto text-center w-full' },
      // Logo
      [
        'img',
        {
          src: '/assets/lithe-logo.svg',
          class:
            'w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 mx-auto mb-6 sm:mb-8',
          alt: 'LitheJS Logo',
        },
      ],
      // Headline
      [
        'h1',
        {
          class:
            'text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6 leading-tight',
        },
        t('hero.headline'),
      ],
      // Subheadline
      [
        'p',
        {
          class:
            'text-lg sm:text-xl md:text-2xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto px-2 leading-relaxed',
        },
        t('hero.subheadline'),
      ],
      // CTA Buttons
      [
        'div',
        {
          class:
            'flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-4 sm:px-0',
        },
        Button(
          {
            variant: 'default',
            size: 'lg',
            classes: 'w-full sm:w-auto',
            onclick: () => goto('/tutorial'),
          },
          t('hero.cta.getStarted')
        ),
        Button(
          {
            variant: 'outline',
            size: 'lg',
            classes: 'w-full sm:w-auto',
            onclick: () => goto('/docs'),
          },
          t('hero.cta.viewDocs')
        ),
      ],
      // Terminal demo
      AnimatedTerminal({
        id: `${id}-terminal`,
        commands: [
          { text: 'npx create-lithejs-app my-app', delay: 100 },
          { text: 'cd my-app', delay: 50 },
          { text: 'npm run dev', delay: 50 },
        ],
        class: 'max-w-xl mx-auto w-full',
      }),
    ],
  ];
};

export default HeroSection;
